import gql from "graphql-tag";
import _ from "underscore";

import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import Log, {Errors} from "@khan/logging";

import Bloodhound from "./bloodhound2.js";
import type {BloodhoundResult} from "./types.ts";

/**
 * A Bloodhound datasource which provides search suggestions given a substring.
 *
 * These suggestions are bulkloaded all at once, indexed locally,
 * and heavily cached.
 */
export const suggestionSource: any = new Bloodhound({
    // At most this many suggestions will be provided
    limit: 3,

    // This tells Bloodhound how to index a datum. We tell it to split up
    // whatever's in the value field (using whitespace as a delimiter) and
    // index each word.
    datumTokenizer: Bloodhound.tokenizers.obj.whitespace("value"),

    // Does an equivalent operation on the query. Bloodhound will then look
    // for datums that have the same tokens as the query (prefixes will
    // also be matched).
    queryTokenizer: Bloodhound.tokenizers.whitespace,

    // We initialize the source with an empty array, but then we fetch
    // the data through GraphQL.
    local: [],
});

// Initialize the first time we ever get eval'ed.
suggestionSource.initialize();

export const GET_SUGGESTION_SOURCE_QUERY: GqlOperation<Data, Variables> = gqlOp(
    gql`
        query GetSuggestionsSource($version: String!) {
            queryCounts(version: $version) {
                name
                count
            }
        }
    `,
);

const reportError = (error: Error) => {
    const reason = error && error.message ? error.message : "<UNKNOWN>";
    const message = `Could not fetch search's typeahead suggestions. Reason: ${reason}`;

    Log.error(
        "Could not fetch search's typeahead suggestions.",
        Errors.Internal,
        {
            cause: error,
            sentryData: {
                fingerprint: [message],
            },
        },
    );
};

type Data = {
    queryCounts: Array<{
        name: string;
        count: number;
    }>;
};

type Variables = {
    version: string;
};

// Pull down the entire collection of suggestions in one go and then
// have Bloodhound search itself (instead of doing remote queries for example).
export const initSuggestions = (gqlClient: ApolloGqlClient) => {
    gqlClient
        .query(GET_SUGGESTION_SOURCE_QUERY, {
            variables: {version: "3"},
            fetchPolicy: "network-only",
        })
        .then(({data}) => {
            if (data?.queryCounts) {
                const suggestions: Array<BloodhoundResult> =
                    data.queryCounts.map((queryCount) => {
                        const query = queryCount.name;
                        return {
                            value: query,
                            count: queryCount.count,
                            conversionIds: ["autocomplete_suggestion"],
                            href:
                                "/search?page_search_query=" +
                                encodeURIComponent(query),
                        };
                    });
                suggestionSource.add(suggestions);
            }
        })
        .catch(reportError);
};
